/**
 * useReview Hook
 *
 * Custom hook for managing review state and operations.
 * Handles fetching review data, polling status, and mutations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Review, ReviewStatusResponse, VerificationPayload, ExportResponse } from '../types';
import * as mhrApi from '../lib/api';

// Polling configuration
const INITIAL_POLL_INTERVAL = 2000; // Start at 2 seconds
const MAX_POLL_INTERVAL = 30000; // Max 30 seconds with exponential backoff
const MAX_POLL_ERRORS = 5; // Max consecutive errors before stopping
const POLL_TIMEOUT = 300000; // 5 minutes total timeout for polling

interface UseReviewReturn {
  review: Review | null;
  loading: boolean;
  error: string | null;
  statusPolling: ReviewStatusResponse | null;
  isPolling: boolean;
  pollingError: string | null; // Separate polling error state
  retryPolling: () => void; // Manual retry function
  verify: (resultId: string, payload: VerificationPayload) => Promise<void>;
  complete: () => Promise<void>;
  cancel: () => Promise<void>;
  exportReview: () => Promise<ExportResponse>;
  refetch: () => Promise<void>;
}

export function useReview(reviewId: string | undefined): UseReviewReturn {
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusPolling, setStatusPolling] = useState<ReviewStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [pollingError, setPollingError] = useState<string | null>(null);

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<number>(INITIAL_POLL_INTERVAL);
  const consecutiveErrorsRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  // Fetch review data
  const fetchReview = useCallback(async () => {
    if (!reviewId) {
      setLoading(false);
      setError('No review ID provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await mhrApi.getReview(reviewId);

      if (isMountedRef.current) {
        setReview(data);
        setLoading(false);
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        const message = err.response?.data?.error || err.message || 'Failed to load review';
        setError(message);
        setLoading(false);
      }
    }
  }, [reviewId]);

  // Stop polling
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollStartTimeRef.current = null;
    pollIntervalRef.current = INITIAL_POLL_INTERVAL;
    consecutiveErrorsRef.current = 0;
  }, []);

  // Poll review status (for in-progress reviews) with exponential backoff
  const pollStatus = useCallback(async () => {
    if (!reviewId || !isMountedRef.current) return;

    // Check total polling timeout
    if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > POLL_TIMEOUT) {
      console.log('[useReview] Polling timeout reached');
      setPollingError('Analysis is taking longer than expected. Please refresh the page to check the status.');
      stopPolling();
      return;
    }

    try {
      const status = await mhrApi.getReviewStatus(reviewId);

      if (isMountedRef.current) {
        setStatusPolling(status);
        setPollingError(null);
        consecutiveErrorsRef.current = 0;
        // Reset interval on successful poll
        pollIntervalRef.current = INITIAL_POLL_INTERVAL;

        // If review completed or failed, stop polling and refetch full review
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          stopPolling();
          // Fetch the full review data
          await fetchReview();
        } else {
          // Schedule next poll
          pollingTimeoutRef.current = setTimeout(pollStatus, pollIntervalRef.current);
        }
      }
    } catch (err: any) {
      console.error('[useReview] Error polling status:', err);
      consecutiveErrorsRef.current++;

      if (consecutiveErrorsRef.current >= MAX_POLL_ERRORS) {
        setPollingError('Unable to check review status. Please check your network connection and try again.');
        stopPolling();
      } else {
        // Exponential backoff on error
        pollIntervalRef.current = Math.min(
          pollIntervalRef.current * 2,
          MAX_POLL_INTERVAL
        );
        console.log(`[useReview] Polling error, retrying in ${pollIntervalRef.current}ms (attempt ${consecutiveErrorsRef.current}/${MAX_POLL_ERRORS})`);
        // Schedule retry with backoff
        pollingTimeoutRef.current = setTimeout(pollStatus, pollIntervalRef.current);
      }
    }
  }, [reviewId, fetchReview, stopPolling]);

  // Start polling if review is in progress
  const startPolling = useCallback(() => {
    if (pollingTimeoutRef.current) return; // Already polling

    setIsPolling(true);
    setPollingError(null);
    pollStartTimeRef.current = Date.now();
    pollIntervalRef.current = INITIAL_POLL_INTERVAL;
    consecutiveErrorsRef.current = 0;
    pollStatus(); // Initial poll (will schedule subsequent polls)
  }, [pollStatus]);

  // Manual retry function for when polling stops due to errors
  const retryPolling = useCallback(() => {
    setPollingError(null);
    consecutiveErrorsRef.current = 0;
    pollIntervalRef.current = INITIAL_POLL_INTERVAL;
    pollStartTimeRef.current = Date.now();
    setIsPolling(true);
    pollStatus();
  }, [pollStatus]);

  // Initial fetch on mount
  useEffect(() => {
    isMountedRef.current = true;
    fetchReview();

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [fetchReview, stopPolling]);

  // Start/stop polling based on review status
  useEffect(() => {
    if (review) {
      const inProgressStatuses = [
        'pending',
        'budget_pending',
        'in_progress',
        'pass_1_exclusions',
        'pass_2_inclusions',
        'pass_3_verification',
      ];

      if (inProgressStatuses.includes(review.status)) {
        startPolling();
      } else {
        stopPolling();
      }
    }
  }, [review?.status, startPolling, stopPolling]);

  // Verify a criterion result
  const verify = useCallback(async (resultId: string, payload: VerificationPayload) => {
    if (!reviewId) throw new Error('No review ID');

    await mhrApi.verifyCriterion(reviewId, resultId, payload);

    // Update local state optimistically
    setReview(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        criteria_results: prev.criteria_results.map(result => {
          if (result.id === resultId) {
            return {
              ...result,
              human_verified: true,
              human_result: payload.human_result,
              human_notes: payload.notes,
            };
          }
          return result;
        }),
      };
    });
  }, [reviewId]);

  // Complete the review
  const complete = useCallback(async () => {
    if (!reviewId) throw new Error('No review ID');

    const completedReview = await mhrApi.completeReview(reviewId);
    setReview(completedReview);
  }, [reviewId]);

  // Cancel the review
  const cancel = useCallback(async () => {
    if (!reviewId) throw new Error('No review ID');

    await mhrApi.cancelReview(reviewId);
    stopPolling();

    // Update local state
    setReview(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: 'cancelled',
      };
    });
  }, [reviewId, stopPolling]);

  // Export review as PDF
  const exportReviewFn = useCallback(async (): Promise<ExportResponse> => {
    if (!reviewId) throw new Error('No review ID');

    return await mhrApi.exportReview(reviewId);
  }, [reviewId]);

  // Refetch review data
  const refetch = useCallback(async () => {
    await fetchReview();
  }, [fetchReview]);

  return {
    review,
    loading,
    error,
    statusPolling,
    isPolling,
    pollingError,
    retryPolling,
    verify,
    complete,
    cancel,
    exportReview: exportReviewFn,
    refetch,
  };
}

export default useReview;
